/**
 * Which sidebar module each setup section belongs to, plus the extra gates a
 * section needs on top of its module's. Data only — no icons, no React — so the
 * visibility rule can import it without pulling the icon set.
 *
 * This is what keeps the onboarding setup track in sync with the left
 * navigation: icons come from the module, the list is ordered by the module's
 * sidebar position, and a section whose module the event doesn't have is not
 * shown at all — it would send the user to a door that isn't there.
 *
 * ADDITIONAL_FIELDS and FILTERS are lanes INSIDE the guest list, not modules of
 * their own; they inherit the guests module for order/visibility but keep their
 * own icon (three identical guest icons in a row reads as a rendering bug).
 */
import type { Capability } from "@/types/Capability";
import { Plugin } from "@/types/enums/Plugin";
import type { SetupSectionKey } from "@/types/SetupPlan";
import type { SidebarModuleUrl } from "@/components/EventSidebar/sidebarModules";

export const SECTION_MODULE: Record<SetupSectionKey, SidebarModuleUrl> = {
  EMAILS: "messaging",
  WORKFLOWS: "automation",
  // Appointments/offerings are reached through the programme entry (W4/F7) —
  // but they are their own product surface, see SECTION_EXTRA_PLUGIN.
  OFFERING: "programme",
  WEBSITE: "website",
  GOALS: "goals",
  GUESTS: "guests",
  ADDITIONAL_FIELDS: "guests",
  FILTERS: "guests",
  SEATING: "seating",
  CHECKIN: "check-in",
  DOCUMENTS: "documents",
};

/**
 * Plugin a section needs on top of its module's gate. The programme entry
 * shows for PROGRAM || SLOTTING, but the OFFERING section sets up appointments
 * — a PROGRAM-only event has no appointments at all, so the row would offer
 * setup for a feature that isn't there.
 */
export const SECTION_EXTRA_PLUGIN: Partial<Record<SetupSectionKey, Plugin>> = {
  OFFERING: Plugin.SLOTTING,
};

/**
 * [PW-H2] Setup sections a plugin implies on its own: the non-buildable, single-plugin sections.
 * A tool the user agreed to gets its setup row even when the interview's plan forgot it. Buildable
 * sections (EMAILS, WORKFLOWS, OFFERING, WEBSITE) are deliberately absent: their rows are the
 * direction pack's and the interview's call (EMAILS↔WORKFLOWS pairing A2-F-8/A3-F-8, the
 * bookable-item rule for OFFERING); guest-list lanes have no plugin.
 */
export const SECTION_IMPLIED_BY_PLUGIN: Partial<Record<SetupSectionKey, Plugin>> = {
  GOALS: Plugin.GOALS,
  SEATING: Plugin.SEATING,
  CHECKIN: Plugin.CHECK_IN,
  DOCUMENTS: Plugin.DOCUMENTS,
};

/**
 * Sections that need real content behind their plugin: at least one bookable
 * programme item (an OFFERING-kind programme block). "Programme" and "bookable
 * programme items" are one switch now (PROGRAM ⇔ SLOTTING, pluginToggle.ts),
 * so SLOTTING is on for nearly every event and no longer says "this event
 * does appointments" — an "Appointments" row on every programme event would
 * be the plugin talking, not the organiser. A row that carries a pack
 * proposal (the appointments direction's starter offering) is exempt: that
 * proposal is the reason the row exists.
 */
export const SECTION_NEEDS_BOOKABLE_ITEM: ReadonlySet<SetupSectionKey> = new Set<SetupSectionKey>([
  "OFFERING",
]);

/**
 * Capability a section's own destination guards on beyond the module's read
 * cap. The check-in module shows with `check-in-logs:read`, but this section
 * leads to the DEVICES page, which guards on `check-in-devices:read`.
 */
export const SECTION_EXTRA_CAPABILITY: Partial<Record<SetupSectionKey, Capability>> = {
  CHECKIN: "check-in-devices:read",
};

/**
 * The section keys this frontend can render. Own-property membership, NOT
 * `SECTION_MODULE[key]` — a plan key of "__proto__" resolves to Object.prototype
 * through the prototype chain and would be rendered as a React child (crash).
 * A key outside this set has no title, icon or route either, so it is dropped;
 * a backend that adds one needs the frontend release that maps it.
 */
export const KNOWN_SECTION_KEYS: ReadonlySet<string> = new Set(
  Object.keys(SECTION_MODULE),
);

export function isKnownSectionKey(key: string): key is SetupSectionKey {
  return KNOWN_SECTION_KEYS.has(key);
}

/**
 * Sections Velo can build from a direction-pack proposal. They carry the
 * starter content the create-event interview prepared, so the panel lists them
 * FIRST — sidebar order alone would bury them at the bottom of the scroller
 * behind the manual guest-list lanes.
 */
export const BUILDABLE: ReadonlySet<SetupSectionKey> = new Set([
  "EMAILS",
  "WORKFLOWS",
  "OFFERING",
  "WEBSITE",
]);

/**
 * Sections Velo sets up conversationally but that have NO direction-pack
 * proposal / local direct-build lane (the main-drawer assistant interviews,
 * then creates via its server tools — create_additional_field /
 * create_segment). Disjoint from BUILDABLE.
 */
export const VELO_ONLY: ReadonlySet<SetupSectionKey> = new Set([
  "ADDITIONAL_FIELDS",
  "FILTERS",
]);
